import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../shared/components/Layout';
import { getUsers } from '../users/api';
import { getAuditLogs } from '../audit/api';
import { User, AuditLog } from '../../shared/types';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    recentActions: 0,
  });
  const [recentUsers, setRecentUsers] = useState<User[]>([]);
  const [recentLogs, setRecentLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [usersResponse, logsResponse] = await Promise.all([
          getUsers({ page: 1, perPage: 5 }),
          getAuditLogs({ per_page: 5 }),
        ]);

        const activeCount = usersResponse.data.filter(u => u.is_active).length;
        
        setStats({
          totalUsers: usersResponse.total,
          activeUsers: activeCount,
          recentActions: logsResponse.total,
        });
        setRecentUsers(usersResponse.data);
        setRecentLogs(logsResponse.data);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const StatCard: React.FC<{ title: string; value: number; icon: string; color: string }> = ({
    title,
    value,
    icon,
    color,
  }) => (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-400 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-white mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center text-xl`}>
          {icon}
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400 mt-1">Overview of your platform</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon="◈"
            color="bg-indigo-500/20 text-indigo-400"
          />
          <StatCard
            title="Active Users"
            value={stats.activeUsers}
            icon="◉"
            color="bg-emerald-500/20 text-emerald-400"
          />
          <StatCard
            title="Audit Events"
            value={stats.recentActions}
            icon="◎"
            color="bg-amber-500/20 text-amber-400"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h2 className="font-semibold text-white">Recent Users</h2>
              <Link to="/users" className="text-sm text-indigo-400 hover:text-indigo-300">
                View all →
              </Link>
            </div>
            <div className="divide-y divide-slate-800">
              {recentUsers.map((user) => (
                <div key={user.id} className="px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-white font-medium">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{user.name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                  </div>
                  <span
                    className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}
                  >
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h2 className="font-semibold text-white">Recent Activity</h2>
              <Link to="/audit-logs" className="text-sm text-indigo-400 hover:text-indigo-300">
                View all →
              </Link>
            </div>
            <div className="divide-y divide-slate-800">
              {recentLogs.map((log) => (
                <div key={log.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-white capitalize">
                        {log.action.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-slate-500">
                        by {log.admin?.name || 'Unknown'} • {log.entity_type}
                      </p>
                    </div>
                    <span className="text-xs text-slate-500">
                      {new Date(log.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
